import { fetchNewsList } from '@/api/home/api'
import {
  fetchMStockMarketTrend,
  fetchKdacs,
  fetchKimchiPremium,
  fetchMStockCoinAll,
  fetchMStockMarketCap,
  fetchTradingSignal,
  fetchEtfFlow
} from '@/api/mStock/api'
import { fetchNewsDetail } from '@/api/news/api'
import { keepPreviousData, useInfiniteQuery, useQueries, useQuery } from '@tanstack/react-query'
import { fetchCandle } from '@api/stock/api.js'
import { useCallback } from 'react'
import { fetchCurrentCoinPrice } from '@api/mStock/api.js'
import { MSTOCK_FOCUS_QUERY_OPTIONS, MSTOCK_QUERY_OPTIONS } from './mStockQueryOptions'
import { hasQueryData } from '@utils/hasQueryData.js'

const validateQueryData = (res, getData) => {
  if (!hasQueryData(getData(res))) {
    throw new Error('EMPTY_DATA')
  }

  return res
}

const mStockQueryKeys = {
  all: ['mstock'],
  marketTrend: () => [...mStockQueryKeys.all, 'market-trend'],
  marketCap: (body) => [...mStockQueryKeys.all, body, 'market-cap'],
  news: () => [...mStockQueryKeys.all, 'news'],
  newsHome: (body) => [...mStockQueryKeys.news(), 'newsHome', body],
  newsList: (body) => [...mStockQueryKeys.news(), 'newsList', body],
  newsDetail: (key) => [...mStockQueryKeys.news(), 'newsDetail', key],
  tradingSignal: (body) => [...mStockQueryKeys.all, 'tradingSignal', body],
  etfFlow: () => [...mStockQueryKeys.all, 'etf-flow'],
  coinAll: () => [...mStockQueryKeys.all, 'coin-all'],
  kimchiPremium: () => [...mStockQueryKeys.all, 'kimchiPremium'],
  kdacs: (assetId) => [...mStockQueryKeys.all, 'kdacs', assetId],
  candle: (body) => [...mStockQueryKeys.all, 'candle', body],
  coinPrice: (assetIds) => [...mStockQueryKeys.all, 'coinPrice', assetIds]
}

const selectMStockCoinAll = (data) => {
  return Array.isArray(data?.coins) ? data.coins : Object.values(data?.coins ?? {})
}

export const useMStockCoinAllQuery = () => {
  return useQuery({
    queryKey: mStockQueryKeys.coinAll(),
    queryFn: () => fetchMStockCoinAll(),
    select: selectMStockCoinAll,
    staleTime: 1000 * 60 * 60 * 24,
    ...MSTOCK_QUERY_OPTIONS,
    refetchOnMount: true
  })
}

const selectMarketCap = (data) => {
  const marketCap = data?.all?.marketCap

  return {
    marketCap: Array.isArray(marketCap) ? marketCap : Object.values(marketCap ?? {}),
    lastFetchTime: data?.lastFetchTime ?? null
  }
}

export const useMStockMarketCapQuery = (body = {}) => {
  return useQuery({
    queryKey: mStockQueryKeys.marketCap(body),
    queryFn: () => fetchMStockMarketCap(body),
    refetchInterval: false,
    staleTime: 1000 * 60 * 5,
    placeholderData: keepPreviousData,
    select: selectMarketCap,
    ...MSTOCK_QUERY_OPTIONS
  })
}

const selectTradingSignals = (data) => {
  const exchanges = Object.fromEntries(
    Object.entries(data ?? {}).filter(([, value]) => Array.isArray(value))
  )

  return {
    exchanges,
    lastFetchTime: data?.lastFetchTime ?? null
  }
}

export const useMStockTradingSignalQuery = (body = {}) => {
  return useQuery({
    queryKey: mStockQueryKeys.tradingSignal(body),
    queryFn: () => fetchTradingSignal(body),
    staleTime: 1000 * 60 * 5,
    refetchInterval: false,
    select: selectTradingSignals,
    ...MSTOCK_QUERY_OPTIONS
  })
}

export const useMStockEtfFlowQuery = () => {
  return useQuery({
    queryKey: mStockQueryKeys.etfFlow(),
    queryFn: async () => {
      const res = await fetchEtfFlow()
      return validateQueryData(res, (data) => data?.dataList)
    },
    staleTime: 1000 * 60 * 5,
    ...MSTOCK_QUERY_OPTIONS
  })
}

const selectKimchiPremium = (res) => {
  return {
    items: Array.isArray(res?.dataList) ? res.dataList : [],
    lastFetchTime: res?.lastFetchTime ?? null
  }
}

export const useMStockKimchiPremiumQuery = () => {
  return useQuery({
    queryKey: mStockQueryKeys.kimchiPremium(),
    queryFn: async () => {
      const res = await fetchKimchiPremium()
      return validateQueryData(res, (data) => data?.dataList)
    },
    select: selectKimchiPremium,
    staleTime: 1000 * 60 * 2,
    ...MSTOCK_QUERY_OPTIONS
  })
}

export const useMStockNewsHomeQuery = ({ page = 1, count = 3 } = {}) => {
  const body = { page, count }

  return useQuery({
    queryKey: mStockQueryKeys.newsHome(body),
    queryFn: () => fetchNewsList(body),
    refetchInterval: false,
    staleTime: 0,
    placeholderData: keepPreviousData,
    ...MSTOCK_QUERY_OPTIONS
  })
}

const MAX_NEWS_COUNT = 90

export const useMStockNewsListInfiniteQuery = ({ page = 1, count = 15 } = {}) => {
  const body = { page, count }

  return useInfiniteQuery({
    queryKey: mStockQueryKeys.newsList(body),

    queryFn: ({ pageParam }) =>
      fetchNewsList({
        ...body,
        page: pageParam
      }),

    initialPageParam: page,

    getNextPageParam: (lastPage, allPages, lastPageParam) => {
      const loadedCount = allPages.reduce(
        (total, page) => total + Object.values(page.newsVOS ?? {}).length,
        0
      )

      if (loadedCount >= MAX_NEWS_COUNT) {
        return undefined
      }

      const lastPageItems = Object.values(lastPage?.newsVOS ?? {})

      if (lastPageItems.length < count) {
        return undefined
      }

      return lastPageParam + 1
    },

    staleTime: Infinity,
    ...MSTOCK_FOCUS_QUERY_OPTIONS,
    refetchOnMount: true
  })
}

export const resetMStockNewsList = (queryClient) => {
  const queryKey = [...mStockQueryKeys.news(), 'newsList']

  queryClient.setQueriesData({ queryKey }, (previous) =>
    previous
      ? {
          pages: previous.pages.slice(0, 1),
          pageParams: previous.pageParams.slice(0, 1)
        }
      : previous
  )

  return queryClient.refetchQueries({ queryKey }, { cancelRefetch: false })
}

const selectMStockNewsDetail = ({ news }) => ({
  title: news?.title ?? '',
  pressName: news?.pressNm ?? '',
  publishedAt: news?.updatedDt || news?.publishedDt || '',
  content: news?.text ?? '',
  imageUrls: news?.newsImgList ?? []
})

export const useMStockNewsDetailQuery = (key) => {
  return useQuery({
    queryKey: mStockQueryKeys.newsDetail(key),
    queryFn: () => fetchNewsDetail(key),
    enabled: Boolean(key),
    staleTime: Infinity,
    select: selectMStockNewsDetail,
    ...MSTOCK_FOCUS_QUERY_OPTIONS
  })
}

export const useMStockKdacsQuery = ({ assetId }) => {
  return useQuery({
    queryKey: mStockQueryKeys.kdacs(assetId),
    queryFn: () => fetchKdacs({ assetId }),
    enabled: Boolean(assetId),
    staleTime: Infinity,
    ...MSTOCK_QUERY_OPTIONS
  })
}

export const useMStockMarketTrendQuery = () => {
  return useQuery({
    queryKey: mStockQueryKeys.marketTrend(),
    queryFn: () => fetchMStockMarketTrend(),
    enabled: true,
    refetchInterval: false,
    staleTime: 1000 * 60,
    placeholderData: keepPreviousData,
    select: useCallback(
      ({ global, dominance, cmc }) => ({
        global,
        dominance,
        cmc
      }),
      []
    ),
    ...MSTOCK_QUERY_OPTIONS
  })
}

const selectCandle = ({ candleType, candles }) => ({
  candleType,
  candles
})

const mStockCandleQueryOptions = ({ assetId, interval, marketType = 'ALL' }) => {
  const body = {
    assetId,
    interval,
    marketType
  }

  return {
    queryKey: mStockQueryKeys.candle(body),
    queryFn: () => fetchCandle({ assetId, marketType, interval }),
    staleTime: 0,
    select: selectCandle,
    ...MSTOCK_QUERY_OPTIONS
  }
}

export const useMStockCandleQuery = ({ assetId, interval, marketType = 'ALL', enabled = true }) => {
  return useQuery({
    ...mStockCandleQueryOptions({
      assetId,
      interval,
      marketType
    }),
    enabled: !!assetId && !!interval && enabled
  })
}

export const useMStockCandleQueries = ({ assetIds = [], marketType = 'ALL', interval }) => {
  return useQueries({
    queries: assetIds.map((assetId) => ({
      ...mStockCandleQueryOptions({
        assetId,
        interval,
        marketType
      }),
      enabled: !!assetId && !!interval
    }))
  })
}

const selectCurrentPrices = (data) => {
  const coins = Array.isArray(data?.coins) ? data.coins : []

  return coins.map(({ assetId, vwap, exchanges, koreanName, symbol }) => ({
    assetId,
    vwap,
    exchanges,
    koreanName,
    symbol
  }))
}

export const useCurrentPricesQuery = (assetIds = [], enabled = true) => {
  return useQuery({
    queryKey: mStockQueryKeys.coinPrice(assetIds),
    queryFn: () => fetchCurrentCoinPrice({ assetIds }),
    enabled: assetIds.length > 0 && enabled,
    staleTime: 5 * 1000,
    placeholderData: keepPreviousData,
    select: selectCurrentPrices,
    ...MSTOCK_QUERY_OPTIONS
  })
}
